import React from "react";
import { useLang, useT } from "./i18n";
import { Icon } from "./lib";
import { useTheme } from "./theme";

export function SidebarProfileMenu({ compact, routeKey, onOpen, onLogout, children }: {
  compact: boolean;
  routeKey: string;
  onOpen: () => void;
  onLogout: () => void;
  children: React.ReactNode;
}) {
  const t = useT();
  const { lang, setLang } = useLang();
  const [theme, setTheme] = useTheme();
  const [open, setOpen] = React.useState(false);
  const footer = React.useRef<HTMLDivElement>(null);
  const trigger = React.useRef<HTMLButtonElement>(null);
  const panel = React.useRef<HTMLDivElement>(null);
  const id = React.useId();

  const close = React.useCallback(() => {
    panel.current?.hidePopover();
    setOpen(false);
  }, []);

  React.useLayoutEffect(close, [close, compact, routeKey]);
  React.useEffect(() => {
    const element = panel.current;
    if (!element) return;
    const sync = () => setOpen(element.matches(":popover-open"));
    element.addEventListener("toggle", sync);
    return () => element.removeEventListener("toggle", sync);
  }, []);

  React.useLayoutEffect(() => {
    if (!open || !footer.current || !panel.current) return;
    const element = panel.current;
    const place = () => {
      const rect = footer.current!.getBoundingClientRect();
      const width = Math.min(compact ? 248 : rect.width - 24, window.innerWidth - 16);
      element.style.width = `${width}px`;
      element.style.left = `${Math.max(8, Math.min(compact ? rect.right + 12 : rect.left + 12, window.innerWidth - width - 8))}px`;
      element.style.top = `${Math.max(8, Math.min(compact ? rect.bottom - 8 : rect.top - 8, window.innerHeight - 8) - element.offsetHeight)}px`;
    };
    place();
    element.querySelector<HTMLButtonElement>('[aria-pressed="true"]')?.focus({ preventScroll: true });
    const dismissOnFocus = (event: FocusEvent) => {
      if (event.target instanceof Node && !element.contains(event.target) && !trigger.current?.contains(event.target)) close();
    };
    window.addEventListener("resize", place);
    document.addEventListener("scroll", place, true);
    document.addEventListener("focusin", dismissOnFocus);
    return () => {
      window.removeEventListener("resize", place);
      document.removeEventListener("scroll", place, true);
      document.removeEventListener("focusin", dismissOnFocus);
    };
  }, [open, compact, close]);

  return <div ref={footer} className="sidebar-footer">
    {children}
    <button ref={trigger} type="button" className="sidebar-profile-trigger" aria-label={t("shell.accountMenu")}
      title={t("shell.accountMenu")} aria-haspopup="dialog" aria-expanded={open} aria-controls={id}
      {...{ popovertarget: id }} onClick={() => { if (!open) onOpen(); }}><Icon name="more" size={18} /></button>
    {/* A native top-layer popover also works inside the narrow-screen modal drawer. */}
    <div ref={panel} id={id} {...{ popover: "auto" }} className="sidebar-profile-menu" role="dialog" style={{ visibility: open ? "visible" : "hidden" }}
      aria-label={t("shell.accountMenu")} onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        event.preventDefault();
        event.stopPropagation();
        close();
        trigger.current?.focus();
      }}>
      <div className="sidebar-profile-row sidebar-profile-language">
        <Icon name="globe" size={20} /><span id={`${id}-language`}>{t("lang.label")}</span>
        <div className="sidebar-languages" role="group" aria-labelledby={`${id}-language`}>
          {(["de", "en"] as const).map((code) => <button key={code} type="button" aria-pressed={code === lang}
            onClick={() => setLang(code)}>{code === lang && <Icon name="check" size={14} />}{code.toUpperCase()}</button>)}
        </div>
      </div>
      <button type="button" className="sidebar-profile-row sidebar-profile-theme" role="switch" aria-checked={theme === "dark"}
        onClick={() => setTheme(theme === "light" ? "dark" : "light")}>
        <Icon name="moon" size={20} /><span>{t("sidebar.darkTheme")}</span>
        <span className="sidebar-theme-switch" aria-hidden="true"><span /></span>
      </button>
      <div className="sidebar-profile-signout">
        <button type="button" className="sidebar-profile-row" onClick={() => { close(); onLogout(); }}>
          <Icon name="logout" size={20} /><span>{t("shell.logout")}</span>
        </button>
      </div>
    </div>
  </div>;
}
